/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import InfoIcon from '@mui/icons-material/Info';
import ShopifyListTable from '../../components/CommonComponents/ShopifyListTable';
import { fetchShopifyList, fetchMigrateInventory, fetchShopifyStatus } from '../../store/reducers/createProfile';
import { ToastNotifyError, ToastNotifySuccess } from '../../components/CommonComponents/Toast/ToastNotify';
import rollbar from '../../rollbarConfig';
import shopifyLogo from '../../assets/images/shopifyLogo.svg';
//import TextFeild from '../../components/CommonComponents/TextFeild';


const ShopifySetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // State management
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [selectedShopifyProductIds, setSelectedShopifyProductIds] = useState([]);
  const [productMappings, setProductMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  //const [storeName, setStoreName] = useState('');
  //const [isConnecting, setIsConnecting] = useState(false);

  // Check Shopify connection status
  const checkShopifyStatus = async () => {
    try {
      const res = await dispatch(fetchShopifyStatus());
      const { connected, success } = res?.payload || {};

      if (success) {
        setShopifyConnected(connected || false);
        return connected || false;
      } else {
        setShopifyConnected(false);
        return false;
      }
    } catch (error) {
      rollbar.error("Error checking Shopify status:", error);
      console.error("Shopify status check failed:", error);
      setShopifyConnected(false);
      return false;
    }
  };

  // Fetch Shopify products
  const fetchShopifyProducts = async (pageNo, pageSizeParam, isTableLoading = false) => {
    if (isTableLoading) {
      setTableLoading(true);
    } else {
      setLoading(true);
    }
    
    try {
      const query = `?page=${pageNo}&page_size=${pageSizeParam}`;
      const res = await dispatch(fetchShopifyList(query));
      const { data, success, message } = res?.payload || {};
      
      if (success) {
        const pagination = data?.pagination || {};
        setTotalCount(pagination.total_count || 0);
        const transformedData = data?.products?.map((product, index) => {
          let tags = [];
          if (product.tags) {
            if (typeof product.tags === 'string') {
              const tagArray = product.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
              tags = tagArray?.length > 0 ? [tagArray[0]] : [];
              if (tagArray?.length > 1) {
                tags.push(`+${tagArray?.length - 1}`);
              }
            } else if (Array.isArray(product.tags)) {
              tags = product.tags?.length > 0 ? [product.tags[0]] : [];
              if (product.tags?.length > 1) {
                tags.push(`+${product.tags?.length - 1}`);
              }
            }
          }

          return {
            id: product.id || index,
            shopifyProductId: product?.shopify_product_id || `SP${String(index + 1).padStart(3, '0')}`,
            wanderobeProductid: product?.id || '',
            name: product.product_name || 'N/A',
            description: product.product_description || 'N/A',
            tags: tags,
            status: product.status || 'Active',
            price: product?.price || product?.variants?.[0]?.price || 0,
            totalQuantity: product.total_quantity || product.variants?.reduce((sum, variant) => sum + (variant.inventory_quantity || 0), 0) || 0,
            images: product?.product_images ? product?.product_images : [],
            variantsCount: product?.variant_count || 0,
            isMapped: product.is_mapped || false,
            suggestedCollection: product?.suggested_collection || '',
            suggestedCategories: product?.suggested_categories || '',
            variants: product?.variants_details?.map((variant) => ({
              image: variant?.image || '',
              options: variant?.options || '',
              quantity: variant?.quantity || 0
            })) || []
          };
        }) || [];
        setShopifyProducts(transformedData);
      } else {
        ToastNotifyError(message || 'Failed to fetch Shopify products');
      }
    } catch (error) {
      rollbar.error("Error fetching Shopify list:", error);
      console.error("Shopify list fetch failed:", error);
      ToastNotifyError('Failed to fetch Shopify products');
    } finally {
      if (isTableLoading) {
        setTableLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSelectionChange = useCallback((selectedIds) => {
    setSelectedShopifyProductIds(selectedIds);
  }, []);

  // Handle category and collection changes from the table
  const handleProductMappingChange = useCallback((productId, field, value) => {
    setProductMappings(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage); // Use the page number directly (1-based)
    fetchShopifyProducts(newPage, pageSize, true); // Use table loading
    
    // Smooth scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
    fetchShopifyProducts(1, newPageSize, true); // Use table loading
    
    // Smooth scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };


  // Handle save product (connect Shopify store)
  const handleSaveProduct = async () => {
    window.open('https://apps.shopify.com/wanderobe', '_self');
    // if (!storeName.trim()) {
    //   ToastNotifyError('Please enter store name');
    //   return;
    // }

    // setIsConnecting(true);
    // try {
    //   const res = await dispatch(fetchShopifyProfile(`?shop=${storeName}`));
    //   const { success, message, auth_url } = res?.payload;
    //   if (success) {
    //     if (auth_url) {
    //       window.location.href = auth_url;
    //     } else {
    //       ToastNotifySuccess(message);
    //       // Refresh the status after successful connection
    //       await checkShopifyStatus();
    //     }
    //   } else if (res.payload.response.data.message === "You already have an active Shopify connection") {
    //     ToastNotifySuccess("Shopify store already connected!");
    //     await checkShopifyStatus();
    //   } else {
    //     ToastNotifyError(message);
    //   }
    // } catch (error) {
    //   rollbar.error("Error in Shopify profile fetch:", error);
    //   console.error("Shopify profile fetch failed:", error);
    //   ToastNotifyError('Failed to connect Shopify store');
    // } finally {
    //   setIsConnecting(false);
    // }
  };

  // Handle migrate inventory
  const handleMigrateInventory = async () => {
    if (selectedShopifyProductIds.length === 0) {
      ToastNotifyError('Please select at least one product to migrate');
      return;
    }

    try {
      // Prepare the data structure for migration
      const products = selectedShopifyProductIds.map(shopifyProductId => {
        const product = shopifyProducts.find(p => p.shopifyProductId === shopifyProductId);
        const mapping = productMappings[product?.id] || {};
        
        return {
          shopify_product_id: parseInt(product?.wanderobeProductid || shopifyProductId),
          mapping_data: {
            collection_id: mapping.collection || null,
            categories: mapping.categories || []
          }
        };
      });

      const migrationData = { products };

      const res = await dispatch(fetchMigrateInventory(migrationData));
      const { success, summary } = res?.payload?.data || {};
      if (success) {
        ToastNotifySuccess(`${summary?.successful} Products migrated successfully`);
        // Clear selections and redirect to dashboard
        setSelectedShopifyProductIds([]);
        setProductMappings({});
        navigate('/dashboard');
      } else {
        ToastNotifyError('Failed to migrate products');
      }
    } catch (error) {
      rollbar.error("Error migrating inventory:", error);
      console.error("Migration failed:", error);
      ToastNotifyError('Failed to migrate products');
    }
  };

  // Initialize productMappings with suggested values when data is loaded
  useEffect(() => {
    if (shopifyProducts && shopifyProducts.length > 0) {
      const initialMappings = {};
      
      shopifyProducts.forEach(product => {
        const categories = product.suggestedCategories || [];
        const collection = product.suggestedCollection || '';
        
        // Convert suggested categories to IDs if they're objects
        let categoryIds = [];
        if (Array.isArray(categories) && categories.length > 0) {
          categoryIds = categories.map(cat => {
            if (typeof cat === 'object' && cat.id) {
              return cat.id;
            }
            return cat;
          });
        }
        
        // Convert suggested collection to ID if it's an object
        let collectionId = '';
        if (typeof collection === 'object' && collection.id) {
          collectionId = collection.id;
        } else if (collection) {
          collectionId = collection;
        }
        
        if (categoryIds.length > 0 || collectionId) {
          initialMappings[product.id] = {
            categories: categoryIds,
            collection: collectionId
          };
        }
      });
      
      if (Object.keys(initialMappings).length > 0) {
        setProductMappings(prev => ({
          ...prev,
          ...initialMappings
        }));
      }
    }
  }, [shopifyProducts]);

  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      // First check Shopify connection status
      const isConnected = await checkShopifyStatus();
      
      // Only fetch products if Shopify is connected
      if (isConnected) {
        fetchShopifyProducts(1, 10);
      } else {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  return (
    <Box>
      <Box sx={{ 
        backgroundColor: '#FFFFFF', 
      }}>
        
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100dvh' 
          }}>
            <Typography>Loading Shopify products...</Typography>
          </Box>
        ) : !shopifyConnected ? (
          <Box sx={{ 
            minHeight: '70dvh',
            p: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={shopifyLogo} alt="shopify" style={{ width: "26px", height: "30px" }} />
                <Typography sx={{ fontSize: '20px', fontWeight: 400, fontFamily: 'Lexend', color: '#000000' }}>
                  Shopify Inventory Setup
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 300, fontFamily: 'Lexend', color: '#656565', mt: '7px' }}>
                Effortlessly set up your store and organize your inventory.
              </Typography>

                <Box className='text-feilds' sx={{ mt: '32px', width: '60%' }}>
                {/* <TextFeild
                  id='storeName'
                  required
                  label="Store Name"
                  placeholder='Enter store name'
                  variant='outlined'
                  width='100%'
                  margin='normal'
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                /> */}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '10px' }}>
                <InfoIcon sx={{ color: '#656565', fontSize: '24px' }} />
                <Typography sx={{ fontSize: '16px', fontWeight: 300, fontFamily: 'Lexend', color: '#656565' }}>
                  Connect your Shopify store to import items automatically, or enter product details manually.
                </Typography>
              </Box>
            </Box>
                         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', mt: '10px' }}>
               <Button 
                 variant='contained' 
                 color='primary' 
                 onClick={handleSaveProduct}
                 sx={{ 
                   width: '100%', 
                   maxWidth: '718px', 
                   padding: '10px', 
                   margin: '20px 0px 20px 0px', 
                   fontSize: '16px', 
                   fontWeight: 400, 
                   textTransform: 'none', 
                   borderRadius: '0px' 
                 }}
               >
                 {'Connect Shopify'}
               </Button>
             </Box>
          </Box>
        ) : shopifyProducts?.length > 0 ? (
          <ShopifyListTable 
            data={shopifyProducts}
            onSelectionChange={handleSelectionChange}
            selectedItems={selectedShopifyProductIds}
            totalCount={totalCount}
            pageNo={page}
            pageSize={pageSize}
            setPageNo={handlePageChange}
            setPageSize={handlePageSizeChange}
            showPagination={true}
            tableLoading={tableLoading}
            onProductMappingChange={handleProductMappingChange}
            productMappings={productMappings}
            headingSx={{pl: '16px', pr: '16px'}}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography variant="h6" sx={{ color: '#666666' }}>
              No Shopify products found
            </Typography>
            <Typography variant="body2" sx={{ color: '#999999' }}>
              No products available in your Shopify store
            </Typography>
          </Box>
        )}
        {shopifyConnected && shopifyProducts?.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: '40px' }}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleMigrateInventory}
              disabled={selectedShopifyProductIds.length === 0}
              sx={{
                width: '100%',
                maxWidth: '718px',
                padding: '10px',
                margin: '20px 0px 0px 0px',
                fontSize: '16px',
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '0px',
              }}
            >
              Update Inventory ({selectedShopifyProductIds.length} selected)
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ShopifySetup;